import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    validateSessionOnLoad,
    isAuthenticated,
    clearAuthData,
} from '../utils/auth';
import { WORKSPACE_ROOT_ADMIN_VIEW } from '../config/adminViewSession';

const isAdminViewPath = (pathname) =>
    String(pathname || '').startsWith(`${WORKSPACE_ROOT_ADMIN_VIEW}/`);

/**
 * AuthProvider Component
 *
 * Manages global authentication state for cookie-based sessions.
 * 1. On mount, validates the session via the core-api /auth/firebase-token
 *    endpoint (doubles as session check + Firebase SDK sign-in).
 * 2. On route change, verifies that local user context still exists.
 *    Actual cookie validity is enforced server-side and caught by the
 *    global 401 interceptor in api.js.
 */
const AuthProvider = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const isInitializedRef = useRef(false);

    const publicRoutes = ['/login', '/signup', '/privacy-policy'];

    const requiresAuth = () => {
        const currentPath = location.pathname;
        if (publicRoutes.includes(currentPath)) return false;
        /* An admin view carries its own credential and has no candidate session to validate.
           Without this it is bounced to /login on mount — inside the admin portal's iframe,
           where a login form is the last thing that belongs. It is not a public route: the
           candidate API still refuses every request on it that is not a verified admin's. */
        if (isAdminViewPath(currentPath)) return false;
        return true;
    };

    useEffect(() => {
        if (isInitializedRef.current) return;

        const initializeAuth = async () => {
            /* An admin view has no candidate session to validate and must not go looking for
               one: the call reaches core-api as an unauthenticated caller, takes a 401 and
               would sign the Firebase SDK into this origin on somebody else's behalf if it
               ever succeeded. There is nothing here for it to restore. */
            if (isAdminViewPath(location.pathname)) {
                isInitializedRef.current = true;
                return;
            }

            const isValid = await validateSessionOnLoad();

            if (!isValid && requiresAuth()) {
                const returnUrl = encodeURIComponent(location.pathname);
                navigate(`/login?returnTo=${returnUrl}`);
            }

            isInitializedRef.current = true;
        };

        initializeAuth();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!isInitializedRef.current) return;

        if (requiresAuth() && !isAuthenticated()) {
            clearAuthData();
            const returnUrl = encodeURIComponent(location.pathname);
            navigate(`/login?returnTo=${returnUrl}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    return <>{children}</>;
};

export default AuthProvider;
